import { debounce } from 'tiny-throttle';
import { REGISTERED_APPS } from '../shared/types/Apps';
import { logError, logInfo } from '../shared/utils/logging';
import {
  CandyCartAppInfo,
  CandyRackAppInfo,
} from '../shared/types/CustomWindow';

type ButtonPair = {
  hiddenButton: HTMLInputElement;
  clonedButton: HTMLInputElement;
};

function hideButton(button: HTMLElement) {
  button.setAttribute(
    'style',
    'display:none!important;visibility:hidden;position:absolute;left:-10000px;',
  );
}

export default class CloningButtonsUtilsBase {
  buttonPairs: ButtonPair[] = [];
  eventName: string;
  registeredApp: REGISTERED_APPS;

  constructor(eventName: string, registeredApp: REGISTERED_APPS) {
    this.eventName = eventName;
    this.registeredApp = registeredApp;
    this.buttonWatcher();
    this.cloneButtons(this.registeredApp);
  }

  buttonWatcher() {
    throw new Error('Not implemented');
  }

  watchButtonRemoval(targetNode: HTMLElement) {
    let target = targetNode;
    function findInsertedButton(addedNodes: Node[], hiddenButton: HTMLElement) {
      return addedNodes.find(
        (n) =>
          n.nodeName === hiddenButton.nodeName &&
          n.nodeType === hiddenButton.nodeType,
      );
    }

    const callback = (mutationsList: MutationRecord[]) => {
      for (const mutation of mutationsList) {
        if (mutation.type === 'childList') {
          for (var node of Array.from(mutation.removedNodes)) {
            if (node === target) {
              logInfo(
                `${target.tagName}#${target.id} is being removed. Updating reference.`,
                this.registeredApp,
              );
              const buttonPair = this.getPairByHiddenButton(target);
              if (!buttonPair) {
                logError('Failed to get hidden button pair');
                return;
              }
              const insertedButton = findInsertedButton(
                mutationsList.flatMap((e) => Array.from(e.addedNodes)),
                buttonPair.hiddenButton,
              );
              if (!insertedButton) {
                logError('Failed to find inserted button');
                return;
              }
              buttonPair.hiddenButton = insertedButton as HTMLInputElement;
              target = buttonPair.hiddenButton;
              hideButton(target);

              buttonPair.clonedButton.className =
                buttonPair.hiddenButton.className;
              buttonPair.clonedButton.innerHTML =
                buttonPair.hiddenButton.innerHTML;
              if (buttonPair.clonedButton instanceof HTMLButtonElement) {
                buttonPair.clonedButton.disabled = (
                  buttonPair.hiddenButton as HTMLButtonElement
                ).disabled;
              }
            }
          }
        }
      }
    };

    try {
      const observer = new MutationObserver(callback);
      if (targetNode.parentElement) {
        observer.observe(targetNode.parentElement, { childList: true });
      }
    } catch (error) {
      logError(
        'Failed to setup button removal observer',
        this.registeredApp,
        error,
      );
    }
  }

  watchButtonChanges(
    targetNode: HTMLInputElement,
    registeredApp: REGISTERED_APPS,
  ) {
    const callback = () => {
      logInfo(
        `${targetNode.id} is being modified. Updating content.`,
        registeredApp,
      );
      const buttonPair = this.getPairByHiddenButton(targetNode);
      if (!buttonPair) {
        logError('Failed to get hidden button pair', registeredApp);
        return;
      }
      const { clonedButton, hiddenButton } = buttonPair;
      if (hiddenButton.style.display !== 'none') {
        hideButton(hiddenButton);
      }
      clonedButton.className = hiddenButton.className;
      clonedButton.innerHTML = hiddenButton.innerHTML;
      if (clonedButton instanceof HTMLButtonElement) {
        clonedButton.disabled = (hiddenButton as HTMLButtonElement).disabled;
        clonedButton.value = (hiddenButton as HTMLButtonElement).value;
      }
    };

    try {
      const observer = new MutationObserver(debounce(callback, 300));

      observer.observe(targetNode, {
        childList: true,
        attributes: true,
        subtree: true,
        attributeFilter: ['disabled', 'className', 'style', 'value'],
      });
    } catch (error) {
      logError('Failed to setup button changes observer', registeredApp, error);
    }
  }

  getButtons(): HTMLInputElement[] {
    throw new Error('Not implemented');
  }

  handleButtonFailure(pair: ButtonPair): void {
    // We need to check for the windowObject like this, so that
    // we are sure to read it correctly
    const windowAppInfo: CandyRackAppInfo | CandyCartAppInfo =
      this.registeredApp === REGISTERED_APPS.ADD_TO_CART
        ? window.CandyRack
        : window.CandyCart;
    if (!windowAppInfo?.buttonState || windowAppInfo?.buttonState === 'idle') {
      const currentApp: 'CandyRack' | 'CandyCart' =
        this.registeredApp === REGISTERED_APPS.ADD_TO_CART
          ? 'CandyRack'
          : 'CandyCart';
      logInfo(
        `cloneButton::buttonClick::${currentApp}.buttonState ${windowAppInfo?.buttonState}`,
        this.registeredApp,
      );
      windowAppInfo.buttonState = 'failed';
      pair.hiddenButton?.click();
    }
  }

  cloneButtons(registeredApp: REGISTERED_APPS) {
    const buttons = this.getButtons();
    logInfo(`found ${buttons.length} buttons`, registeredApp);
    buttons.forEach((button) => {
      button.classList.add('candy');

      const cloned = button.cloneNode(true) as HTMLElement;
      cloned.classList.add('candy-cloned');
      button.insertAdjacentElement('afterend', cloned);
      hideButton(button);
      this.watchButtonRemoval(button);
      this.watchButtonChanges(button, registeredApp);

      this.buttonPairs.push({
        clonedButton: cloned as HTMLInputElement,
        hiddenButton: button as HTMLInputElement,
      });
    });

    const buttonClicked = (event: MouseEvent) => {
      const target = (event.currentTarget || event.target) as HTMLElement;
      const pair = this.getPairByClonedButton(target);
      if (!pair) {
        logError(
          "Couldn't find pair for cloned button.",
          registeredApp,
          target,
        );
        return;
      }

      if (!pair.clonedButton.classList.contains('cr-ignore')) {
        event.preventDefault();
        event.stopImmediatePropagation();

        logInfo(`dispatching ${this.eventName}`, registeredApp);

        document.dispatchEvent(
          new CustomEvent(this.eventName, { detail: pair }),
        );
        setTimeout(() => {
          this.handleButtonFailure(pair);
        }, 500);
      }
    };

    this.buttonPairs.forEach(({ clonedButton }) => {
      clonedButton.addEventListener('click', buttonClicked, { capture: true });
    });
    return this.buttonPairs;
  }

  getPairByHiddenButton(button: HTMLInputElement | HTMLElement) {
    return this.buttonPairs.find((pair) => pair.hiddenButton === button);
  }

  getPairByClonedButton(clonedButton: HTMLInputElement | HTMLElement) {
    return this.buttonPairs.find((pair) => pair.clonedButton === clonedButton);
  }
}
